export interface TimelineStep {
  step: string;
  title: string;
  meta: string;
  statusWord: string;
  statusSub: string;
  side: "left" | "right";
  accentColor: string;
  accentBg: string;
  accentBorder: string;
}

export interface FeatureItem {
  icon: string;
  title: string;
  desc: string;
  bg?: string;
  fg?: string;
}

export interface FAQItem {
  q: string;
  a: string;
}

export interface NhcxTranslations {
  heroEyebrow: string;
  heroTitle: string;
  heroDesc: string;
  bookDemo: string;
  sectionEyebrow: string;
  sectionTitle: string;
  featuresEyebrow: string;
  featuresTitle: string;
  faqEyebrow: string;
  faqTitle: string;
  faqDesc: string;
  lifecycleEyebrow: string;
  lifecycleTitle: string;
  lifecycleDesc: string;
  howClaimIqWorks: string;
  lifecycleSteps: TimelineStep[];
  features: FeatureItem[];
  faqs: FAQItem[];
}

export const getNhcxData = (locale: string): NhcxTranslations => {
  const getSteps = (lang: string): TimelineStep[] => {
    if (lang === "hi") {
      return [
        {
          step: "01",
          title: "पूर्व-सहमति पैकेट भेजा गया",
          meta: "ClaimIQ ने 102 नैदानिक ​​दस्तावेजों को स्वचालित रूप से बंडल और सत्यापित किया।",
          statusWord: "बंडल किया गया",
          statusSub: "102 दस्तावेज़ सत्यापित",
          side: "left",
          accentColor: "#0A9E9A",
          accentBg: "#DFFAF8",
          accentBorder: "rgba(10,158,154,0.22)",
        },
        {
          step: "02",
          title: "पूर्व-सहमति स्वीकृत",
          meta: "बीमाकर्ता ने NHCX के माध्यम से तुरंत ₹84,000 स्वीकृत किए।",
          statusWord: "स्वीकृत",
          statusSub: "₹84,000",
          side: "right",
          accentColor: "#2A9E5B",
          accentBg: "#EAF7EE",
          accentBorder: "rgba(42,158,91,0.22)",
        },
        {
          step: "03",
          title: "मरीज डिस्चार्ज हुआ",
          meta: "अंतिम बिल और डिस्चार्ज सारांश स्वचालित रूप से संकलित।",
          statusWord: "डिस्चार्ज",
          statusSub: "बिल संकलित",
          side: "left",
          accentColor: "#B88500",
          accentBg: "#FFF4DE",
          accentBorder: "rgba(184,133,0,0.22)",
        },
        {
          step: "04",
          title: "अंतिम दावा दायर",
          meta: "ICD-10 कोड और अस्पताल दरों का सख्ती से सत्यापन किया गया।",
          statusWord: "सत्यापित",
          statusSub: "दरें मेल खाती हैं",
          side: "right",
          accentColor: "#6D28D9",
          accentBg: "#F6F2FF",
          accentBorder: "rgba(109,40,217,0.18)",
        },
        {
          step: "05",
          title: "बीमाकर्ता निर्णय",
          meta: "दावा समीक्षा में है। ClaimIQ अस्वीकृति जोखिम: 4%।",
          statusWord: "कम जोखिम",
          statusSub: "4%",
          side: "left",
          accentColor: "#CC2B58",
          accentBg: "#FCE5EC",
          accentBorder: "rgba(204,43,88,0.22)",
        },
        {
          step: "06",
          title: "अंतिम निपटान",
          meta: "अस्पताल खाते में राशि सीधे जमा की गई (अनुमानित T+2)।",
          statusWord: "निपटारा",
          statusSub: "₹84,000",
          side: "right",
          accentColor: "#2A9E5B",
          accentBg: "#EAF7EE",
          accentBorder: "rgba(42,158,91,0.22)",
        },
      ];
    }

    if (lang === "ar") {
      return [
        {
          step: "01",
          title: "إرسال حزمة الموافقة المسبقة",
          meta: "قام نظام ClaimIQ بتجميع وتدقيق 102 وثيقة سريرية تلقائياً.",
          statusWord: "مجمعة",
          statusSub: "تم تدقيق 102 وثيقة",
          side: "left",
          accentColor: "#0A9E9A",
          accentBg: "#DFFAF8",
          accentBorder: "rgba(10,158,154,0.22)",
        },
        {
          step: "02",
          title: "الموافقة المسبقة المعتمدة",
          meta: "وافق المؤمن على ₹84,000 فوراً عبر NHCX.",
          statusWord: "معتمدة",
          statusSub: "₹84,000",
          side: "right",
          accentColor: "#2A9E5B",
          accentBg: "#EAF7EE",
          accentBorder: "rgba(42,158,91,0.22)",
        },
        {
          step: "03",
          title: "خروج المريض",
          meta: "تجميع الفاتورة النهائية وملخص الخروج تلقائياً.",
          statusWord: "تم الخروج",
          statusSub: "تم تجميع الفاتورة",
          side: "left",
          accentColor: "#B88500",
          accentBg: "#FFF4DE",
          accentBorder: "rgba(184,133,0,0.22)",
        },
        {
          step: "04",
          title: "تقديم المطالبة النهائية",
          meta: "التحقق بدقة من رموز ICD-10 وتعرفات المستشفى.",
          statusWord: "تم التحقق",
          statusSub: "مطابقة الأكواد",
          side: "right",
          accentColor: "#6D28D9",
          accentBg: "#F6F2FF",
          accentBorder: "rgba(109,40,217,0.18)",
        },
        {
          step: "05",
          title: "دراسة المطالبة",
          meta: "المطالبة قيد المراجعة. نسبة مخاطر الرفض: 4%.",
          statusWord: "مخاطر منخفضة",
          statusSub: "4%",
          side: "left",
          accentColor: "#CC2B58",
          accentBg: "#FCE5EC",
          accentBorder: "rgba(204,43,88,0.22)",
        },
        {
          step: "06",
          title: "التسوية النهائية",
          meta: "إيداع المبلغ في حساب المستشفى (المتوقع خلال يومين).",
          statusWord: "تمت التسوية",
          statusSub: "₹84,000",
          side: "right",
          accentColor: "#2A9E5B",
          accentBg: "#EAF7EE",
          accentBorder: "rgba(42,158,91,0.22)",
        },
      ];
    }

    // Default: English
    return [
      {
        step: "01",
        title: "Pre-Auth Packet Sent",
        meta: "ClaimIQ auto-bundled & validated 102 clinical docs.",
        statusWord: "BUNDLED",
        statusSub: "102 Docs Validated",
        side: "left",
        accentColor: "#0A9E9A",
        accentBg: "#DFFAF8",
        accentBorder: "rgba(10,158,154,0.22)",
      },
      {
        step: "02",
        title: "Pre-Auth Sanctioned",
        meta: "Insurer approved ₹84,000 instantly via NHCX.",
        statusWord: "SANCTIONED",
        statusSub: "₹84,000",
        side: "right",
        accentColor: "#2A9E5B",
        accentBg: "#EAF7EE",
        accentBorder: "rgba(42,158,91,0.22)",
      },
      {
        step: "03",
        title: "Patient Discharged",
        meta: "Final bill & discharge summary automatically compiled.",
        statusWord: "DISCHARGED",
        statusSub: "Final Bill Compiled",
        side: "left",
        accentColor: "#B88500",
        accentBg: "#FFF4DE",
        accentBorder: "rgba(184,133,0,0.22)",
      },
      {
        step: "04",
        title: "Final Claim Filed",
        meta: "ICD-10 codes and hospital tariffs strictly verified.",
        statusWord: "VERIFIED",
        statusSub: "ICD & Tariff Matched",
        side: "right",
        accentColor: "#6D28D9",
        accentBg: "#F6F2FF",
        accentBorder: "rgba(109,40,217,0.18)",
      },
      {
        step: "05",
        title: "Insurer Adjudication",
        meta: "Claim in review. ClaimIQ rejection risk: 4%.",
        statusWord: "LOW RISK",
        statusSub: "4%",
        side: "left",
        accentColor: "#CC2B58",
        accentBg: "#FCE5EC",
        accentBorder: "rgba(204,43,88,0.22)",
      },
      {
        step: "06",
        title: "Final Settlement",
        meta: "Amount directly credited to hospital account (Expected T+2).",
        statusWord: "SETTLED",
        statusSub: "₹84,000",
        side: "right",
        accentColor: "#2A9E5B",
        accentBg: "#EAF7EE",
        accentBorder: "rgba(42,158,91,0.22)",
      },
    ];
  };

  if (locale === "hi") {
    return {
      heroEyebrow: "NHCX · PMJAY · ClaimIQ",
      heroTitle: "दावे जो पहली बार में ही स्वीकृत हो जाएं।",
      heroDesc: "चिकित्सा दावा-कार्यप्रवाह-रेडी HIMS है - NHCX दावा प्रवाह, PMJAY पूर्व-प्राधिकरण, दस्तावेज़ की उपलब्धता, ClaimIQ AI सत्यापन, दावा स्थिति ट्रैकिंग और तेज़ भुगतान, सब एक ही स्थान पर।",
      bookDemo: "डेमो बुक करें",
      sectionEyebrow: "दावा जीवनचक्र",
      sectionTitle: "शुरू से अंत तक पूरी तरह से ट्रैक किया गया।",
      featuresEyebrow: "क्या शामिल है",
      featuresTitle: "आपकी बीमा और TPA टीम की प्रत्येक आवश्यकता का समाधान।",
      faqEyebrow: "अक्सर पूछे जाने वाले प्रश्न",
      faqTitle: "NHCX और दावा प्रोसेसिंग अक्सर पूछे जाने वाले प्रश्न।",
      faqDesc: "सत्यापन, अस्वीकृति दर में कमी और भुगतान समयरेखा पर संक्षिप्त उत्तर।",
      lifecycleEyebrow: "NHCX दावा जीवनचक्र",
      lifecycleTitle: "पूर्व-सहमति से लेकर भुगतान तक, हर कदम पर नजर।",
      lifecycleDesc: "प्रत्येक दावा इवेंट NHCX के माध्यम से प्रवाहित होता है। ClaimIQ AI जमा करने से पहले दस्तावेजों का सत्यापन करता है ताकि आपकी टीम की मेहनत बेकार न हो।",
      howClaimIqWorks: "ClaimIQ कैसे काम करता है",
      lifecycleSteps: getSteps("hi"),
      features: [
        { icon: "receipt", title: "NHCX दावा प्रवाह", desc: "चिकित्सा में अंतर्निहित देशी NHCX सबमिशन, प्रतिक्रिया और मिलान।" },
        { icon: "shield", title: "PMJAY पूर्व-प्राधिकरण", desc: "लाभार्थी सत्यापन, पात्रता जांच और पूर्व-प्राधिकरण दस्तावेज़ कार्यप्रवाह।", bg: "var(--rose-soft)", fg: "var(--rose)" },
        { icon: "sparkle", title: "ClaimIQ AI", desc: "स्वचालित सत्यापन, लापता दस्तावेज़ पहचान और अस्वीकृति-जोखिम स्कोरिंग।", bg: "#EFE9F8", fg: "var(--plum-2)" },
        { icon: "chart", title: "स्थिति ट्रैकिंग", desc: "दावे, डॉक्टर, विभाग और बीमाकर्ता के अनुसार लाइव डैशबोर्ड।", bg: "var(--mint-soft)", fg: "#1A6033" },
        { icon: "bolt", title: "अस्वीकृति में कमी", desc: "97% पहली बार में स्वीकृति दर · अस्वीकृत दावों पर रीवर्क में ↓ 64% की कमी।", bg: "#FFF4DE", fg: "#7A5300" },
        { icon: "wallet", title: "तेज़ निपटान", desc: "साफ़-सुथरे दावे = पूर्वानुमेय नकदी प्रवाह · दावा चक्र में ↓ 11 दिनों की बचत।" },
        { icon: "file", title: "दस्तावेज़ तत्परता", desc: "स्वचालित रूप से संकलित डिस्चार्ज सारांश, जांच रिपोर्ट, बिल और सहमति।", bg: "var(--rose-soft)", fg: "var(--rose)" },
        { icon: "users", title: "TPA कार्यक्षेत्र", desc: "TPA, बीमा डेस्क और वित्त टीम के लिए कार्य सूची - SLAs और ऑडिट लॉग के साथ।", bg: "#EFE9F8", fg: "var(--plum-2)" },
      ],
      faqs: [
        { q: "क्या चिकित्सा (Chikitsa) NHCX के अनुकूल है?", a: "हाँ। चिकित्सा (Chikitsa) दावा जीवनचक्र के सभी चरणों का समर्थन करता है - पूर्व-सहमति, दावा सबमिशन, भुगतान स्थिति और वित्तीय मिलान।" },
        { q: "ClaimIQ पहली बार में दावों की स्वीकृति दर कैसे सुधारता है?", a: "ClaimIQ दस्तावेजों में कमियां और विसंगतियां पहले ही पकड़ लेता है, जिससे अस्वीकृति दर कम होती है।" },
        { q: "क्या यह PMJAY पूर्व-प्राधिकरण का समर्थन करता है?", a: "हाँ। EMR, डॉक्टरों के पर्चे और अस्पताल के दरों से पूर्व-प्राधिकरण फ़ोल्डर स्वतः तैयार होता है।" },
      ],
    };
  }

  if (locale === "ar") {
    return {
      heroEyebrow: "NHCX · PMJAY · ClaimIQ",
      heroTitle: "مطالبات تتم الموافقة عليها من المرة الأولى.",
      heroDesc: "تشيكيتسا نظام HIMS جاهز لدعم دورة حياة مطالبات التأمين - مطالبات NHCX، والموافقات المسبقة، والتحقق عبر ذكاء ClaimIQ الاصطناعي.",
      bookDemo: "احجز عرضاً تجريبياً",
      sectionEyebrow: "دورة حياة المطالبة",
      sectionTitle: "تتبع المطالبة خطوة بخطوة بالكامل.",
      featuresEyebrow: "المزايا المتضمنة",
      featuresTitle: "كل ما يحتاجه فريق التأمين ومكتب TPA في مكان واحد.",
      faqEyebrow: "الأسئلة الشائعة",
      faqTitle: "الأسئلة الشائعة حول مطالبات التأمين و NHCX.",
      faqDesc: "إجابات وحلول حول نسب الموافقة، التحقق بالذكاء الاصطناعي والتسويات المالية.",
      lifecycleEyebrow: "دورة حياة مطالبات NHCX",
      lifecycleTitle: "من الموافقة المسبقة إلى التسوية المالية، تحت المتابعة.",
      lifecycleDesc: "تأكد من تسوية مطالباتك بشكل أسرع بفضل التدقيق التلقائي قبل تقديم المطالبة.",
      howClaimIqWorks: "كيف يعمل ClaimIQ",
      lifecycleSteps: getSteps("ar"),
      features: [
        { icon: "receipt", title: "مطالبات NHCX", desc: "تقديم ومتابعة وتسوية المطالبات بشكل متكامل وتلقائي." },
        { icon: "shield", title: "موافقات PMJAY", desc: "التحقق من أهلية المرضى وتجهيز ملف الموافقة المسبقة بالكامل.", bg: "var(--rose-soft)", fg: "var(--rose)" },
        { icon: "sparkle", title: "ذكاء ClaimIQ", desc: "تدقيق تلقائي للوثائق وحساب نسبة مخاطر رفض المطالبة.", bg: "#EFE9F8", fg: "var(--plum-2)" },
        { icon: "chart", title: "تتبع المطالبة", desc: "لوحة بيانات للمطالبات والتحصيل حسب الطبيب، القسم والشركة.", bg: "var(--mint-soft)", fg: "#1A6033" },
        { icon: "bolt", title: "تقليل الرفض", desc: "معدل موافقة 97% من المرة الأولى وتخفيض 64% في تعديل المطالبات.", bg: "#FFF4DE", fg: "#7A5300" },
        { icon: "wallet", title: "تسوية أسرع", desc: "مطالبات نظيفة تعني تدفقاً مالياً متوقعاً لعمليات المستشفى." },
      ],
      faqs: [
        { q: "هل يدعم النظام دورة موافقات PMJAY المسبقة؟", a: "نعم. يتم سحب ملف الموافقة تلقائياً من الفحوصات والتقرير الطبي وتعرفات الفواتير." },
      ],
    };
  }

  // Fallback to English / Spanish / French
  return {
    heroEyebrow: "NHCX · PMJAY · ClaimIQ",
    heroTitle: "Claims that pass, the first time.",
    heroDesc: "Chikitsa is a claim-workflow-ready HIMS - NHCX claim flows, PMJAY pre-auth, document readiness, ClaimIQ AI validation, claim status tracking and faster settlement, all in one place.",
    bookDemo: "Book a Demo",
    sectionEyebrow: "Timeline",
    sectionTitle: "Reconciliation and claims timeline.",
    featuresEyebrow: "What's included",
    featuresTitle: "Everything your TPA & insurance team has been asking for.",
    faqEyebrow: "Frequently Asked",
    faqTitle: "NHCX and claims questions answered.",
    faqDesc: "Answers on integrations, ClaimIQ approval rates, and PMJAY pre-auth.",
    lifecycleEyebrow: "NHCX claim lifecycle",
    lifecycleTitle: "From pre-auth to settlement, tracked end to end.",
    lifecycleDesc: "Every claim event flows through NHCX. ClaimIQ AI validates documents and codes before submission and predicts rejection risk.",
    howClaimIqWorks: "How ClaimIQ works",
    lifecycleSteps: getSteps("en"),
    features: [
      { icon: "receipt", title: "NHCX claim flows", desc: "Native NHCX submission, response and reconciliation built into Chikitsa." },
      { icon: "shield", title: "PMJAY pre-auth", desc: "Beneficiary verification, eligibility and pre-auth document workflows.", bg: "var(--rose-soft)", fg: "var(--rose)" },
      { icon: "sparkle", title: "ClaimIQ AI", desc: "Automated validation, missing-doc detection & rejection-risk scoring.", bg: "#EFE9F8", fg: "var(--plum-2)" },
      { icon: "chart", title: "Status tracking", desc: "Live dashboards by claim, doctor, department & insurer - drill all the way down.", bg: "var(--mint-soft)", fg: "#1A6033" },
      { icon: "bolt", title: "Reduced rejection", desc: "97% first-pass approval typical · ↓ 64% rework on rejected claims.", bg: "#FFF4DE", fg: "#7A5300" },
      { icon: "wallet", title: "Faster settlement", desc: "Cleaner claims = predictable cash flow · ↓ 11 days in claim cycle." },
      { icon: "file", title: "Document readiness", desc: "Auto-bundled discharge summary, investigations, bill & consent.", bg: "var(--rose-soft)", fg: "var(--rose)" },
      { icon: "users", title: "TPA workspace", desc: "Workqueues for TPA, insurance desk & finance - with SLAs & audit.", bg: "#EFE9F8", fg: "var(--plum-2)" },
    ],
    faqs: [
      { q: "Is Chikitsa NHCX compliant?", a: "Yes. Chikitsa supports the full NHCX claim lifecycle - pre-auth, claim, response, payment and reconciliation - with native NHCX message formats and onboarded TPA/insurer connectivity." },
      { q: "How does ClaimIQ improve first-pass approval?", a: "ClaimIQ is Chikitsa's claim AI. It runs automated validation, missing-document detection and rejection-risk scoring before submission. Hospitals using ClaimIQ typically see 97% first-pass approval." },
      { q: "Does Chikitsa support PMJAY pre-auth?", a: "Yes. Beneficiary verification, eligibility checks, document readiness and claim status tracking are first-class workflows." },
      { q: "Can our existing TPA team work in Chikitsa?", a: "Yes. The TPA workspace gives queues, SLAs and audit logs to your insurance desk and finance team without changing existing roles." },
      { q: "How much does the claim cycle shrink?", a: "Hospitals typically see a ↓ 11-day reduction in the claim cycle and a ↓ 64% drop in rework on rejected claims after wiring up ClaimIQ." },
    ],
  };
};
